import type { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Tag, Typography } from 'antd';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text } = Typography;

interface PageHeaderProps {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  count?: number | string;
  countLabel?: string;
  actions?: ReactNode;
  // When provided, render a subtle gradient strip above the title.
  accent?: 'ink' | 'violet' | 'green' | 'amber';
  // Topic key for the help drawer. Renders an inline (?) hotspot next to
  // the title so the salient concept on each page is one click from a real
  // explanation.
  helpTopic?: string;
}

// Page header used on every list/detail screen for visual consistency.
//
// Layout (single row, vertically tight):
//
//   ▎ icon  Title · count · (?)        Subtitle (truncated)         [actions...]
//
// Height: ~48px on a typical page. Previously the subtitle wrapped to
// 4-5 lines on narrow viewports because it competed with wide action
// rows; now it's clipped to one line with a Tooltip carrying the full
// text on hover. The accent line is a 2px gradient bar above — picked
// up from the Linear / Vercel pattern where every primary surface has
// a subtle vertical rhythm marker. Optional, opt-in per page.

const ACCENT_GRADIENTS: Record<NonNullable<PageHeaderProps['accent']>, string> = {
  ink: `linear-gradient(90deg, ${colors.ink[600]}, ${colors.violet[600]})`,
  violet: `linear-gradient(90deg, ${colors.violet[600]}, ${colors.ink[500]})`,
  green: `linear-gradient(90deg, ${colors.green[600]}, ${colors.ink[500]})`,
  amber: `linear-gradient(90deg, ${colors.amber[600]}, ${colors.red[500]})`,
};

export const PageHeader = observer(function PageHeader({
  icon,
  title,
  subtitle,
  count,
  countLabel,
  actions,
  accent,
  helpTopic,
}: PageHeaderProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  // Detect a string subtitle so we can render it as a hover-tooltip
  // when truncated. ReactNode subtitles fall through and render inline
  // (rare; handful of pages pass a span or fragment).
  const subtitleString = typeof subtitle === 'string' ? subtitle : null;

  return (
    <div style={{ marginBottom: 4 }}>
      {accent && (
        <div
          style={{
            height: 2,
            width: 56,
            borderRadius: 2,
            background: ACCENT_GRADIENTS[accent],
            marginBottom: 8,
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          rowGap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
          {icon && (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: isDark ? colors.slate[700] : colors.ink[50],
                color: colors.ink[600],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              {icon}
            </div>
          )}
          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', minWidth: 0 }}>
              {/*
                Use a plain <h4> instead of Typography.Title so AntD's
                .ant-typography selectors (which include word-break /
                overflow-wrap rules at higher specificity than our inline
                whiteSpace) don't override the single-line truncation. The
                h4 styling is set inline so we keep the Typography
                semantics without inheriting AntD's wrap behavior.
              */}
              <h4
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  // maxWidth here is wide-but-bounded so a truly long
                  // title still clips rather than pushing the layout.
                  maxWidth: 520,
                  color: isDark ? colors.slate[100] : colors.slate[900],
                }}
                title={typeof title === 'string' ? title : undefined}
              >
                {title}
              </h4>
              {helpTopic && (
                <HelpHotspot
                  topic={helpTopic}
                  variant="inline"
                  label={typeof title === 'string' ? `Learn about ${title.toLowerCase()}` : 'Learn more'}
                />
              )}
              {count != null && (
                <Tag
                  style={{
                    margin: 0,
                    fontWeight: 500,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 11,
                    background: isDark ? colors.slate[700] : colors.slate[100],
                    color: isDark ? colors.slate[200] : colors.slate[600],
                    borderColor: 'transparent',
                    flexShrink: 0,
                  }}
                >
                  {count}
                  {countLabel ? ` ${countLabel}` : ''}
                </Tag>
              )}
            </div>
            {subtitle && (
              subtitleString ? (
                // Use AntD's built-in ellipsis API — it handles the
                // CSS specificity correctly and produces the same single-
                // line + tooltip behavior we want. Inline whiteSpace
                // tricks were unreliable when AntD's .ant-typography
                // overrode them.
                <Text
                  type="secondary"
                  ellipsis={{ tooltip: subtitleString }}
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.4,
                    maxWidth: 480,
                    cursor: 'default',
                  }}
                >
                  {subtitleString}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 12.5, lineHeight: 1.4 }}>
                  {subtitle}
                </Text>
              )
            )}
          </div>
        </div>
        {actions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
});
